import React, { useCallback, useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { useDrop } from 'react-dnd';
import { v4 as uuidv4 } from 'uuid';
import { withTranslation } from 'react-i18next';
import { Icon, Tooltip, message } from 'antd';
import { CreativeBlockContainer } from '../styles';
import BlockHighlight from '../BlockHighlight';
import BlockElementRenderer from './BlockElementRenderer';
import HeightResizableDiv from '../HeightResizableDiv';
import { useDocumentContext, COMPLEX_DOCUMENT_CONTEXT_KEYS } from '../../../context/complexDocumentContext';
import { COMPLEX_PAGE_OPTIONS } from '../translationValues';
import { processIfSVG } from '../../../utils/svgUtils';
import { NativeTypes } from 'react-dnd-html5-backend';
import { useArtboardContext, CONTEXT_KEYS, TEMPLATE_TYPE } from '../../../context/complexArtboardContext';
import ComplexEditingProvider from '../../../context/complexEditingContext';
import {
  HEADER,
  FOOTER,
  RESIZE_HANDLE,
  FOOTER_HANDLE,
  dummyCallback,
  ELEMENTS,
  CREATIVE_BLOCK,
  TEXT_ELEMENT,
  IMAGE_ELEMENT,
  SVG_ELEMENT,
  BARCODE_ELEMENT,
  FILLABLE_TEXT_INPUT_TYPE,
  OPTIONS_KEY,
  FIELD_NAME_KEY,
  FILLABLE_ELEMENT,
  FILLABLE_SIGNATURE_TYPE,
  FILLABLE_COMBO_BOX_TYPE,
  FILLABLE_RADIO_BUTTON_GROUP_TYPE,
  FILLABLE_RADIO_BUTTON_TYPE,
  FILLABLE_CHECKBOX_TYPE,
  ELEMENT_TYPES,
  STATIC_TABLE_ELEMENT,
  COMPLEX_DOCUMENT_PAGE_WRAPPER,
  STATIC_TABLE_BLOCK,
  UNSUPPORTED_REPEAT_ELEMENTS,
  BARCODE_39
} from '../../../utils/complexDocumentConstants';
import PageElementsProvider from '../../../context/pageElementsContext';
import PageSettingsProvider from '../../../context/pageSettingsContext';
import CreativeDesignContextWrapper from './CreativeDesignContextWrapper';
import { uploadImage } from '../../../utils/services/uploadAPI';
import { getImageSize } from '../../../utils/javascript';
import { VARIABLE_TYPE } from '../../../utils/constants/appConstants';
import { GroupNameModal } from '../../../artboard/ContextMenu/GroupNameModal';

const DocumentEditingContextProvider = ({ children, parentElements, setParentElements, initialPageSetting = undefined, ...props }) => {
  return (
    <PageSettingsProvider initialPageSetting={initialPageSetting}>
      <PageElementsProvider isEmail={true} isComplexDesign={true} parentElements={parentElements} setParentElements={setParentElements}>
        {children}
      </PageElementsProvider>
    </PageSettingsProvider>
  );
}

const CreativeBlockDesign = (props) => {
  const { t, editable, itemUUID, dependentRef, parentUUID, pageId, type, scaling, getElementStyles, adjustScroll, blockIsEditable = true, onClickBlockSelection= dummyCallback, isStandaloneFlow, isMobileView = false, isInteractiveDesign = false } = props;
  const blockRef = useRef(null);
  const dropElementFnRef = useRef(null);
  const isCurrentBlock = useRef(false);
  const moveableReference = useRef(undefined);

  const customSelector = useCallback(store => store[itemUUID], []);
  const [creativeBlock, methods] = useDocumentContext(customSelector, `DesignBlock-${itemUUID}`);

  const customVarSelector = useCallback(store => store[COMPLEX_DOCUMENT_CONTEXT_KEYS.GLOBAL_VARIABLES], []);
  const [globalVariables] = useDocumentContext(customVarSelector);

  const contentRef = useRef(creativeBlock?.contents?.map(el => ({ uuid: el.uuid, type: el.contentType })));
  const [clearSelection, setClearSelection] = useState(false);

  const artboardContextSelector = useCallback(store => store[CONTEXT_KEYS.SWITCH_CREATIVE_DESIGN_MENU] && store[CONTEXT_KEYS.CURRENT_BLOCK] === itemUUID, []);
  const [currentBlock, updateArtboard] = useArtboardContext(artboardContextSelector, `CreativeBlockDesign-${itemUUID}`);

  const blockAvtiveContextSelector = useCallback(store => store[CONTEXT_KEYS.CURRENT_BLOCK] === itemUUID, []);
  const [isBlockActive] = useArtboardContext(blockAvtiveContextSelector, `CreativeBlockDesign-${itemUUID}`);

  const settingsSelector = useCallback(store => store[CONTEXT_KEYS.ELEMENT_SETTINGS_VISIBLE], []);
  const [settingsVisible] = useArtboardContext(settingsSelector, `CreativeBlockDesign-${itemUUID}`);

  const clickedElementSelector = useCallback(store => store[CONTEXT_KEYS.ADD_ELEMENT_TO_CREATIVE_BLOCK], []);
  const [elementClicked] = useArtboardContext(clickedElementSelector, `CreativeBlockDesign-add-element-${itemUUID}`)

  const blockTypeSelector = useCallback(store => store[COMPLEX_DOCUMENT_CONTEXT_KEYS.REUSABLE_BLOCK_TYPE], []);
  const [reusableBlockType] = useDocumentContext(blockTypeSelector, `LetsDesignTabDetails`);

  const currentDesignSelector = useCallback((store) => [TEMPLATE_TYPE.DOCUMENT, TEMPLATE_TYPE.FLOW].includes(store[CONTEXT_KEYS.CURRENT_DESIGN]), []);
  const [isDocumentOrFlowDesign] = useArtboardContext(currentDesignSelector, 'CreativeBlockDesign');

  const groupNameSelector = useCallback(store => store[CONTEXT_KEYS.GROUP_NAME_MODAL], []);
  const [showGroupName] = useArtboardContext(groupNameSelector);

  const [selectedElementIds, setSelectedElementIds] = useState([]);
  const [selectedGroupElementId, setSelectedGroupElementId] = useState();

  const selectedBlockSelector = useCallback(store => store[CONTEXT_KEYS.SELECTED_BLOCKS], []);
  const [selectedBlocks] = useArtboardContext(selectedBlockSelector, `TextBlock-${itemUUID}`);

  const actionItemSelector = useCallback(store => store[CONTEXT_KEYS.GROUP_ACTION_ITEM], []);
  const [actionItem] = useArtboardContext(actionItemSelector);

  const [isBlockNotFits, setIsBlockNotFits] = useState(false);

  useEffect(() => {
    const flowElement= document.getElementById(parentUUID);
    const checkIfBlockFitContentArea = (e) => {
      const {detail} = e;
      const flow = methods.get()[parentUUID];
      const headerHeight = methods.get()[flow[HEADER]]?.style?.height || 0;
      const footerHeight = methods.get()[flow[FOOTER]]?.style?.height || 0;
      const creativeBlockHeight = creativeBlock?.style?.height || 0; 
      const newContentAreaHeight = detail?.newContentAreaHeight || ((flow.height || undefined) - ((flow.margin[0] || 0) + (flow.margin[2] || 0) + headerHeight + footerHeight));
      const blockheight = detail.blockId === itemUUID ? (detail?.updatedbBlockheight || creativeBlockHeight) : creativeBlockHeight;
      setIsBlockNotFits(creativeBlock.type=== CREATIVE_BLOCK && blockheight > newContentAreaHeight);
    };
    
    flowElement.addEventListener('checkCreativeBlockHeight', checkIfBlockFitContentArea);

    flowElement.dispatchEvent(new CustomEvent('checkCreativeBlockHeight', { detail: {} }));
    
    return () => {
      flowElement.removeEventListener('checkCreativeBlockHeight', checkIfBlockFitContentArea);
    };
  }, []);
  const [currentPageUUID] = useArtboardContext(
    useCallback(store => store[CONTEXT_KEYS.CURRENT_PAGE], []),
    'creativeBlockDesign',
  );
  useEffect(() => {
    const handleEnter = () => {
      const element = document.querySelector(".moveable-area");
      if (element) {
        element.style.visibility = "hidden";
      }
    };
    const handleLeave = () => {
      const element = document.querySelector(".moveable-area");
      if (element) {
        element.style.visibility = "";
      }
    };
    if (selectedGroupElementId) {
      const element = document.querySelector(`[data-uuid='${selectedGroupElementId}'][data-element-type='text']`) || document.querySelector(`[data-uuid='${selectedGroupElementId}'][data-element-type='static_table']`) || document.querySelector(`[data-uuid='${selectedGroupElementId}'][data-element-type='text_input']`);
      if (element) {
        element.addEventListener("mouseenter", handleEnter);
        element.addEventListener("mouseleave", handleLeave);
      }
    }
    return () => {
      if (selectedGroupElementId) {
        const element = document.querySelector(`[data-uuid='${selectedGroupElementId}'][data-element-type='text']`) || document.querySelector(`[data-uuid='${selectedGroupElementId}'][data-element-type='static_table']`) || document.querySelector(`[data-uuid='${selectedGroupElementId}'][data-element-type='text_input']`);
        if (element) {
          element.removeEventListener("mouseenter", handleEnter);
          element.removeEventListener("mouseenter", handleLeave);
        }
      }
    }
  }, [selectedGroupElementId]);

  const pageSettingsSelector = useCallback(
    store => {
      if (currentBlock && currentPageUUID && currentPageUUID !== '') {
        const pageWidth = store[currentPageUUID]?.width;
        const pageHeight = store[currentPageUUID]?.height;
        const size = {
          widthInPX: pageWidth,
          heightInPX: pageHeight,
        };
        return {
          size,
        };
      }
      return {};
    },
    [currentBlock, currentPageUUID],
  );
  const [pageSettings] = useDocumentContext(pageSettingsSelector, 'creativeBlockDesign');

  useEffect(() => {
    isCurrentBlock.current = currentBlock;
  }, [currentBlock]);

  useEffect(() => {
    // TODO : check unmount is called in any scenario while editing other elements
    const creativeBlockRef = document.getElementById(itemUUID);
    creativeBlockRef.addEventListener('pageScrollEnd', e => {
      if (moveableReference.current) {
        moveableReference.current.updateRect();
      }
    });
    return () => {
      if (isCurrentBlock.current) {
        Array.from(document.querySelectorAll(`#dim-bg-${pageId}`)).forEach(node => {
          node.style.display = 'none';
        });
        updateArtboard({ [CONTEXT_KEYS.SWITCH_CREATIVE_DESIGN_MENU]: false, [CONTEXT_KEYS.CURRENT_BLOCK]: '', [CONTEXT_KEYS.CURRENT_BLOCK_TYPE]: '' });
      }
    }
  }, []);

  useEffect(() => {
    contentRef.current = creativeBlock.contents.map(el => ({ uuid: el.uuid, type: el.contentType }));
  }, [creativeBlock.contents]);

  const updateSidebar = useCallback(() => {
    updateArtboard({
      [CONTEXT_KEYS.SWITCH_CREATIVE_DESIGN_MENU]: true,
      [CONTEXT_KEYS.CURRENT_BLOCK]: itemUUID,
      [CONTEXT_KEYS.CURRENT_BLOCK_TYPE]: CREATIVE_BLOCK,
      [CONTEXT_KEYS.CURRENT_PAGE]: parentUUID,
      [CONTEXT_KEYS.PAGE_LAYOUT_VISIBLE]: false,
      [CONTEXT_KEYS.BLOCK_SETTINGS_VISIBLE]: false
    });
  }, [parentUUID]);

  const STATIC_TYPES = ['header', 'footer'];
  useEffect(() => {
    if (STATIC_TYPES.includes(reusableBlockType) && editable) {
      updateSidebar();
    }
  }, []);

  const blockSelected = selectedBlocks.includes(itemUUID);

  useEffect(() => {
    if (!currentBlock) {
      const creativeBlockRef = document.getElementById(itemUUID);
      if (creativeBlockRef.scrollTop !== 0 || creativeBlockRef.scrollLeft !== 0) {
        creativeBlockRef.scrollTo(0, 0);
      }
      setSelectedElementIds([]);
      updateSelectedGroupElementId();
    }
  }, [currentBlock]);

  useEffect(() => {
    if (!settingsVisible) {
      setSelectedElementIds([]);
      updateSelectedGroupElementId();
      setClearSelection(true);
      setTimeout(() => {
        setClearSelection(false);
      }, 10);
    }
  }, [settingsVisible]);

  const onHeightChange = useCallback(({ height }) => {
    const resizeSuccess = methods.updateBlock('height', itemUUID, { height, flowUUID: parentUUID, type, reusableSection: !isDocumentOrFlowDesign });
    adjustScroll()
    // TODO : Check if height is within limits
    return resizeSuccess;
  }, []);

  const handleExternalFileDrop = useCallback(async (element, monitor) => {
    const allowedExternalFileTypes = ['image/jpeg', 'image/png', 'image/svg+xml', 'image/gif'];
    let uploadedFiles = [];
    if (element.files.length > 0) {
      for (let i = 0; i < element.files.length; i++) {
        if (allowedExternalFileTypes.includes(element.files[i].type)) {
          let dimensions = {};
          getImageSize(element.files[i]).then(value => {
            let { widthInPX, heightInPX } = pageSettings.size;
            let width = value.width;
            let height = value.height;
            if (width > widthInPX || height > heightInPX) {
              let widthRatio = parseInt(width) / (widthInPX / 3);
              let heightRatio = parseInt(height) / (heightInPX / 3);
              const ratio = widthRatio > heightRatio ? widthRatio : heightRatio;
              width = parseInt(width) / ratio;
              height = parseInt(height) / ratio;
            }
            dimensions.height = height;
            dimensions.width = width;
          });
          uploadedFiles.push({ file: element.files[i], dimensions });
        } else {
          message.error(t(COMPLEX_PAGE_OPTIONS.FILE_NOT_SUPPORTED_ERROR_MESSAGE.key, COMPLEX_PAGE_OPTIONS.FILE_NOT_SUPPORTED_ERROR_MESSAGE.value));
        }
      }
    }

    let uploaded = [];
    if (uploadedFiles.length > 0) {
      const loadingInfo = message.loading(t(COMPLEX_PAGE_OPTIONS.UPLOADING_INFO_MESSAGE.key, COMPLEX_PAGE_OPTIONS.UPLOADING_INFO_MESSAGE.value), 0);
      try {
        uploaded = await Promise.all(uploadedFiles.map(({ file, dimensions }) => {
          return (async () => {
            let response = await uploadImage(file, file.name);
            return { response, dimensions };
          })();
        }));
      } catch (error) {
        console.log(error);
      } finally {
        loadingInfo();
      }
    }

    return uploaded;
  }, [pageSettings]);

  const onDrop = async (element, monitor) => {
    if (monitor?.getItemType() === '__NATIVE_FILE__') {
      if(currentBlock) {
        const uploadedImages = await handleExternalFileDrop(element, monitor);
        uploadedImages.forEach(item => {
          const { response, dimensions } = item;
          const { id, imageLink } = response;
          const details = {
            type: 'elements',
            element: {
              type: 'colored',
              elementDetails: {
                styles: {
                  position: 'absolute',
                  backgroundColor: 'transparent',
                  height: `${dimensions.height}px`,
                  width: `${dimensions.width}px`,
                },
                type: 'image',
                value: imageLink,
              },
              id,
              name: `upload-${id}`,
              thumbnail: imageLink,
            },
            boundaries: {},
          };
          handleElementDrop(details, monitor);
        });
      }
    } else {
      handleElementDrop(element, monitor);
    }
  }

  const handleElementDrop = useCallback(async (element, monitor) => {
    const item = element.element ? element : Object.assign({}, { element });
    const hasRepeatedBlocks = creativeBlock?.variableContract?.some(
      variable => variable.type === VARIABLE_TYPE.OBJECT,
    );
    if (hasRepeatedBlocks && UNSUPPORTED_REPEAT_ELEMENTS.includes(item.element.elementDetails.type)) {
      message.warn(t(
        COMPLEX_PAGE_OPTIONS.FORM_ELEMENTS_NOT_SUPPORT.key,
        COMPLEX_PAGE_OPTIONS.FORM_ELEMENTS_NOT_SUPPORT.value,
      ));
      return;
    }
    let offset;
    let cursorOffset;
    let elementX;
    if (monitor) {
      if (monitor.didDrop()) return;
      if (monitor.getClientOffset()) {
        offset = monitor.getSourceClientOffset();
        cursorOffset = monitor.getClientOffset();
        elementX = cursorOffset.x;
      }
    }

    const dropTarget = document.getElementById(itemUUID);
    let dropTargetXy = dropTarget.getBoundingClientRect();

    let elementWidth = Number(item.element.elementDetails.styles.width.replace(/px$/, ''));
    let elementHeight = Number(item.element.elementDetails.styles.height.replace(/px$/, ''));

    let extractedTransform = {};
    if (item.element.elementDetails?.styles?.transform) {
      const transformStyles = item.element.elementDetails.styles.transform.split(' ');
      transformStyles.forEach(data => {
        let key = data.split('(')[0];
        let value = data && data.match(/\((.*)\)/)[1];
        value = Number(value.replace(/[^\d.-]/g, ''));
        let pair = { [key]: value };
        extractedTransform = { ...extractedTransform, ...pair };
      });
    }

    let newElement = {
      uuid: uuidv4(),
      contentType: item.element.elementDetails.type,
      style: Object.assign({}, {
        width: item.element.elementDetails.styles.width,
        height: item.element.elementDetails.styles.height,
        rotate: item.element.elementDetails.styles.rotate
      }, item.element.elementDetails?.additionalStyles || {}),
      uiType: item.element.elementDetails.uiType
    };

    newElement.style.width = Number(newElement.style.width.replace(/px$/, ''));
    newElement.style.height = Number(newElement.style.height.replace(/px$/, ''))

    let xOffset = 0;
    let yOffset = 0;
    if (monitor && offset && item) {
      xOffset = cursorOffset.x - offset.x;
      yOffset = cursorOffset.y - offset.y;
      const widthRatio = xOffset / item.boundaries.width;
      xOffset = elementWidth * widthRatio;
      const heightRatio = yOffset / item.boundaries.height;
      yOffset = elementHeight * heightRatio;
      if (scaling >= 1) {
        elementX = cursorOffset.x - xOffset;
        elementX = elementX - (elementX * (scaling - 1));
      }

      newElement.style.x = ((cursorOffset.x - dropTargetXy.left) / scaling) - xOffset;
      newElement.style.y = ((cursorOffset.y - dropTargetXy.top) / scaling) - yOffset;
      newElement.style.rotate = extractedTransform.rotate || 0;
    } else {
      newElement.style.x
        = (dropTargetXy.width / scaling - newElement.style.width) / 2;
      newElement.style.y
        = (dropTargetXy.height / scaling - newElement.style.height) / 2;
      newElement.style.rotate = extractedTransform.rotate || 0;
    }
    newElement.contentType = item.element.elementDetails.type;
    let additionalElements = [];

    // TODO : Apply this only for elements dropped from master design tab - should change while working with reusables
    if (item.element.elementDetails && item.element.elementDetails.type === IMAGE_ELEMENT) {
      const { value, styles, uiType } = item.element['elementDetails'];
      const newValue = await processIfSVG(value, styles, uiType);
      newElement.url = newValue;
      newElement.contentType = value !== newValue ? SVG_ELEMENT : IMAGE_ELEMENT;
    } else if (item.element.elementDetails && item.element.elementDetails.type === BARCODE_ELEMENT) {
      newElement.barcodeType = item.element.elementDetails.barcodeType;
      newElement.style.isVariable = false;
      newElement.content = `Sample ${item.element.elementDetails.barcodeType} barcode`;
      if( item.element.elementDetails.barcodeType === BARCODE_39){
        newElement.content = `SAMPLE ${item.element.elementDetails.barcodeType.toUpperCase()} BARCODE`;
      }
    } else if (item.element.elementDetails.type === TEXT_ELEMENT) {
      // TODO : Convert the text to a delta value appropriate for the new text block element
      newElement.contents = item.element.elementDetails.contents;
    } else if (item.element.elementDetails.type === 'textbox') { // TODO : Check if this value can be changed without affecting existing design system
      newElement.contentType = FILLABLE_ELEMENT;
      newElement.fillableType = FILLABLE_TEXT_INPUT_TYPE;
      newElement.contents = item.element.elementDetails.contents;
      newElement.formOptions = item.element.elementDetails.formOptions;
    } else if (item.element.elementDetails.type === 'signature') { // TODO : Check if this value can be changed without affecting existing design system
      newElement.contentType = FILLABLE_ELEMENT;
      newElement.fillableType = FILLABLE_SIGNATURE_TYPE;
      newElement.content = item.element.elementDetails.content;
      newElement.formOptions = item.element.elementDetails.formOptions;
    } else if (item.element.elementDetails.type === 'combobox') { // TODO : Check if this value can be changed without affecting existing design system
      newElement.contentType = FILLABLE_ELEMENT;
      newElement.fillableType = FILLABLE_COMBO_BOX_TYPE;
      newElement.contents = item.element.elementDetails.contents;
      newElement.formOptions = item.element.elementDetails.formOptions;
    } else if (item.element.elementDetails.type === FILLABLE_CHECKBOX_TYPE) {
      newElement.contentType = FILLABLE_ELEMENT;
      newElement.fillableType = FILLABLE_CHECKBOX_TYPE;
      newElement.formOptions = item.element.elementDetails.formOptions;

      const textElement = item.element.elementDetails.contents.find(
        el => el.contentType === TEXT_ELEMENT,
      );
      textElement.uuid = uuidv4();
      textElement.style = {
        ...textElement.style,
        x: newElement.style.x + textElement.style.x,
        y: newElement.style.y + textElement.style.y,
      };
      additionalElements.push(textElement);
    } else if (item.element.elementDetails.type === FILLABLE_RADIO_BUTTON_TYPE) {
      newElement.contentType = FILLABLE_ELEMENT;
      newElement.fillableType = FILLABLE_RADIO_BUTTON_TYPE;
      newElement.formOptions = item.element.elementDetails.formOptions;

      const textElement = item.element.elementDetails.contents.find(
        el => el.contentType === TEXT_ELEMENT,
      );
      textElement.uuid = uuidv4();
      textElement.style = {
        ...textElement.style,
        x: newElement.style.x + textElement.style.x,
        y: newElement.style.y + textElement.style.y,
      };
      additionalElements.push(textElement);
    } else if (item.element.elementDetails.type === 'radiobuttongroup') {
      newElement.contentType = FILLABLE_ELEMENT;
      newElement.fillableType = FILLABLE_RADIO_BUTTON_GROUP_TYPE;
      newElement.formOptions = item.element.elementDetails.formOptions;
      if (item.element.elementDetails.formOptions.isDynamic === false) {
        const fillableElements = item.element.elementDetails.contents.filter(el => el.contentType === FILLABLE_RADIO_BUTTON_TYPE);
        newElement.contents = fillableElements.map(fillableElement => {
          fillableElement.style = {
            ...newElement.style,
            ...fillableElement.style,
            x: newElement.style.x + fillableElement.style.x,
            y: newElement.style.y + fillableElement.style.y
          };
          return Object.assign({}, { uuid: newElement.uuid }, fillableElement);
        });
        newElement.content = fillableElements[0][OPTIONS_KEY][FIELD_NAME_KEY];

        let textElements = item.element.elementDetails.contents.filter(el => el.contentType === TEXT_ELEMENT);
        textElements = textElements.map(textElement => {
          textElement.style = {
            ...newElement.style,
            ...textElement.style,
            x: newElement.style.x + textElement.style.x,
            y: newElement.style.y + textElement.style.y
          };
          return Object.assign({}, { uuid: uuidv4() }, textElement);
        })
        console.log({textElements, item: item.element});
        additionalElements = additionalElements.concat(textElements);
      }
    } else if(item.element.elementDetails.type === STATIC_TABLE_ELEMENT) {
      newElement.style.border =  item.element.elementDetails.styles.border;
      newElement.style.backgroundColor =   item.element.elementDetails.styles.backgroundColor;
      newElement.style.tableProperties = item.element.elementDetails.styles.tableProperties;
      newElement.contents = item.element.elementDetails.contents.map(inst=> {
        inst.uuid = uuidv4();
        return inst;
      });
    } else if (item.element.elementDetails.type === ELEMENT_TYPES.CHART) {
      const isRepetitiveBlock = creativeBlock?.variableContract?.length
        ? creativeBlock?.variableContract[0]?.type === 'object'
        : false;
      newElement.style.border = item.element.elementDetails.styles.border;
      newElement.style.backgroundColor = item.element.elementDetails.styles.backgroundColor;
      // when dragging height and width will 200 for pie charts thumbnail. Will change to 500 once dropped in the editor.
      newElement.style.height = 500;
      newElement.style.width = 500;
      // adjust the x and y coordinates of chart element to drop in the centre rather than at calculated coordinates.
      newElement.style.x = newElement.style.x - 100;
      newElement.style.y = newElement.style.y - 150;
      newElement.contents = item.element.elementDetails.chartDetails;
      const chartElementsCount = methods.get()[COMPLEX_DOCUMENT_CONTEXT_KEYS.CHART_ELEMENTS_COUNT];
      const updatedChartElementsCount = chartElementsCount + 1;
      newElement.contents.baseVariable = `chartvar-${updatedChartElementsCount}`;
      methods.set({
        [COMPLEX_DOCUMENT_CONTEXT_KEYS.CHART_ELEMENTS_COUNT]: updatedChartElementsCount,
      });
      if (isRepetitiveBlock) {
        newElement.contents.repeatVariable = creativeBlock?.variableContract[0]?.name;
      }
    }

    console.log("Newly dropped element :: ", newElement, additionalElements);

    if (dropElementFnRef.current) {
      dropElementFnRef.current([newElement, ...additionalElements], true)
    }
    // to focus on the complex page wrapper element for keyboard events to work properly
    // after dropping elements in creative block.
    const complexDocumentWrapperEl = document.getElementById(COMPLEX_DOCUMENT_PAGE_WRAPPER);
    complexDocumentWrapperEl && complexDocumentWrapperEl.focus();
  }, [scaling, creativeBlock]);

  useEffect(() => {
    if (currentBlock && elementClicked !== undefined) {
      // Add element to block
      onDrop(elementClicked);
      updateArtboard({ [CONTEXT_KEYS.ADD_ELEMENT_TO_CREATIVE_BLOCK]: undefined });
    }
  }, [currentBlock, elementClicked, onDrop]);

  const handleSelectedElementIds = useCallback((elementIds = []) => {
    if (elementIds.length === 0) {
      updateArtboard({ [CONTEXT_KEYS.ELEMENT_SETTINGS_VISIBLE]: false });
    } else if (elementIds.length === 1) {
      const contentType = contentRef.current.filter(el => el.uuid === elementIds[0])[0]?.type;
      if (contentType === FILLABLE_ELEMENT || contentType === ELEMENT_TYPES.CHART) {
        updateArtboard({ [CONTEXT_KEYS.ELEMENT_SETTINGS_VISIBLE]: elementIds[0] });
      } else {
        updateArtboard({ [CONTEXT_KEYS.ELEMENT_SETTINGS_VISIBLE]: false });
      }
    } else {
      updateArtboard({ [CONTEXT_KEYS.ELEMENT_SETTINGS_VISIBLE]: false });
    }
    setSelectedElementIds(elementIds);
  }, []);

  const updateSelectedGroupElementId = useCallback(
    groupId => {
      setSelectedGroupElementId(groupId);
      if (groupId) {
        const element = creativeBlock.contents.find(element => element.uuid === groupId);
        if (element) {
          const { contentType } = element;
          if (contentType === FILLABLE_ELEMENT || contentType === ELEMENT_TYPES.CHART) {
            updateArtboard({ [CONTEXT_KEYS.ELEMENT_SETTINGS_VISIBLE]: groupId });
          } else {
            updateArtboard({ [CONTEXT_KEYS.ELEMENT_SETTINGS_VISIBLE]: false });
          }
        }
      }
    },
    [creativeBlock],
  );

  const refreshSelectedElementIds = useCallback((elementIds = []) => {
    setClearSelection(true);
    requestAnimationFrame(() => {
      setSelectedElementIds(elementIds);
      setClearSelection(false);
    });
  }, []);

  const [, drop] = useDrop({
    canDrop: () => editable,
    accept: [ELEMENTS, NativeTypes.FILE],
    collect: monitor => !!monitor.isOver({ shallow: true }),
    drop: onDrop
  });

 const updateCreativeBlockElements = useCallback((contents, isGroup) => {
  if(isGroup){
    methods.updateBlock('grouping', creativeBlock.uuid, { contents, flowUUID: parentUUID, type }, true, globalVariables);
  }
  else{
    methods.updateBlock('contents', creativeBlock.uuid, { contents, flowUUID: parentUUID, type }, true, globalVariables);
  }
  }, [parentUUID, creativeBlock.uuid, type, globalVariables]);
  const updateSingleElement = useCallback((element, additionalInfo) => {
    methods.updateBlock('element', creativeBlock.uuid, { element, flowUUID: parentUUID, type, ...additionalInfo }, true, globalVariables);
  }, [parentUUID]);

  const isEmpty = !creativeBlock.contents?.length && !currentBlock;
  const isStaticElement = type === HEADER || type === FOOTER;
  const refProps = isStaticElement ? { ref: drop(blockRef) } : { ref: drop };

  const selectedElement =
    selectedElementIds.length === 1
      ? creativeBlock.contents.filter(el => selectedElementIds.includes(el.uuid))[0]
      : selectedElementIds.length > 1 && selectedGroupElementId ? creativeBlock.contents.find(el => el.uuid === selectedGroupElementId)
      : undefined;

  return (
    <ComplexEditingProvider>
      <DocumentEditingContextProvider parentElements={creativeBlock.contents} setParentElements={updateCreativeBlockElements}>
      <CreativeBlockContainer
        {...refProps}
        id={itemUUID}
        className={`${(isEmpty && isBlockNotFits) ? 'empty block-not-fit' : isEmpty ? 'empty' : isBlockNotFits? 'block-not-fit' : ''}`}
        currentBlock={currentBlock}
        style={{ height: creativeBlock.type === STATIC_TABLE_BLOCK ? 'auto' : creativeBlock.style.height * (isMobileView ? 0.66 : 1), background: blockSelected ? '#ACCEF7' : creativeBlock.style.backgroundColor }}
        onClick={editable && isDocumentOrFlowDesign && !currentBlock ? (e) => {
          if (e.ctrlKey || e.metaKey) {
            onClickBlockSelection({itemUUID, parentUUID})
          }
        } : undefined}
        onDoubleClick={editable && blockIsEditable ? updateSidebar : dummyCallback}
        onMouseMove={(e) => (editable || isInteractiveDesign) && !currentBlock && getElementStyles && getElementStyles(e, itemUUID)}
        onMouseEnter={() => {
          const el = document.getElementById(COMPLEX_DOCUMENT_PAGE_WRAPPER);
          if (el && document.activeElement !== el && !el.contains(document.activeElement)) {
            el.focus();
          }
        }}
        staticTableBlock={creativeBlock.type === STATIC_TABLE_BLOCK}
      >
        {isBlockNotFits && (
          <div className="tag">
            {t(
              COMPLEX_PAGE_OPTIONS.CREATIVE_BLOCK_HEIGHT_OVERFLOW_ERROR_MESSAGE.key,
              COMPLEX_PAGE_OPTIONS.CREATIVE_BLOCK_HEIGHT_OVERFLOW_ERROR_MESSAGE.value,
            )}
            <Tooltip
              title={t(
                COMPLEX_PAGE_OPTIONS.CREATIVE_BLOCK_HEIGHT_OVERFLOW_INFO.key,
                COMPLEX_PAGE_OPTIONS.CREATIVE_BLOCK_HEIGHT_OVERFLOW_INFO.value,
              )}
              placement="bottom"
            >
              <Icon type="info-circle" />
            </Tooltip>
          </div>
        )}
        {currentBlock && creativeBlock.style.backgroundColor === undefined && (
           <BlockHighlight pageUUID={parentUUID} />
        )}
        {editable && isEmpty && type === CREATIVE_BLOCK && t(COMPLEX_PAGE_OPTIONS.EMPTY_CREATIVE_BLOCK_PLACEHOLDER.key, COMPLEX_PAGE_OPTIONS.EMPTY_CREATIVE_BLOCK_PLACEHOLDER.value)}
        {editable && isEmpty && (type === HEADER || type === FOOTER) && t(COMPLEX_PAGE_OPTIONS.EMPTY_COMPLEX_HEADER_OR_FOOTER_PLACEHOLDER.key, COMPLEX_PAGE_OPTIONS.EMPTY_COMPLEX_HEADER_OR_FOOTER_PLACEHOLDER.value)}
        {!isStaticElement && creativeBlock.type !== STATIC_TABLE_BLOCK && <div ref={blockRef} id={`${itemUUID}-ref`} style={{ position: 'absolute', zIndex: -1, pointerEvents: 'none', top: 0, height: creativeBlock.style.height, width: '100%' }} />}
        {creativeBlock.contents.map((element, elementIndex) => {
          return (
            <BlockElementRenderer
              key={`${element.uuid}-${elementIndex}`}
              element={element}
              editable={editable && currentBlock}
              selected={element.uuid === selectedElement?.uuid}
              scaling={scaling}
              pageId={pageId}
              creativeBlock={isStandaloneFlow ? {...creativeBlock, isStandalone:true} :creativeBlock}
              adjustScroll={adjustScroll}
              dataSetKey={creativeBlock?.variableContract?.length && creativeBlock?.variableContract[0]?.type === 'object'
                ? creativeBlock?.variableContract[0]?.name
                : undefined}
              dataIndex={creativeBlock.dataIndex}
              elementIndex={elementIndex}
              updateSingleElement={updateSingleElement}
              isMobileView={isMobileView}
              staticTableBlock={element.type === STATIC_TABLE_BLOCK}
              refreshSelectedElementIds={refreshSelectedElementIds}
            />
          )
        })}
      </CreativeBlockContainer>
      <CreativeDesignContextWrapper
        itemUUID={itemUUID}
        pageId={pageId}
        parentUUID={parentUUID}
        scaling={scaling}
        dropElementFnRef={dropElementFnRef}
        editable={currentBlock}
        selectedElementIds={selectedElementIds}
        setSelectedGroupElementId={updateSelectedGroupElementId}
        selectedGroupElementId={selectedGroupElementId}
        setSelectedElementIds={handleSelectedElementIds}
        clearSelection={clearSelection}
        moveableReference={moveableReference}
        staticTableBlock={creativeBlock.type === STATIC_TABLE_BLOCK}
        showGroupName={isBlockActive && showGroupName}
      />
      </DocumentEditingContextProvider>
      {currentBlock && <HeightResizableDiv
        height={creativeBlock.style.height}
        onResizeStop={onHeightChange}
        handle={type === FOOTER ? FOOTER_HANDLE : RESIZE_HANDLE}
        containerRef={blockRef}
        dependentRef={dependentRef}
        className={`block-resize complex-block-${type === FOOTER ? 'footer' : 'header'}`}
        resizeCallback={props.resizeCallback}
      />}
      {(selectedElement?.contentType === FILLABLE_ELEMENT || selectedElement?.contentType === ELEMENT_TYPES.CHART) && ReactDOM.createPortal(
        <div
          className="layoutbtn-container"
          onClick={() => {
            updateArtboard({ [CONTEXT_KEYS.ELEMENT_SETTINGS_VISIBLE]: false });
            setSelectedElementIds([]);
            updateSelectedGroupElementId();
            setClearSelection(true);
            setTimeout(() => {
              setClearSelection(false);
            }, 10);
          }}
        >
          <Icon type={'close'} />
        </div>,
        document.getElementById('sidebar-absolute-close')
      )}
    </ComplexEditingProvider>
  )
}

export default React.memo(withTranslation('COMPLEX_PAGE_OPTIONS')(CreativeBlockDesign));

